import * as React from 'react';
import { Field, formValueSelector, getFormSyncErrors } from 'redux-form';
import { connect} from 'react-redux';
import { flow, forEachRight, get, isNull, map } from 'lodash';
import { Col, Row} from 'react-bootstrap';
import * as uuid from 'uuid/v4';
import {
  allowOnlyApostrophes,
  hasEmojis,
  hasSpecialCharacters,
  isAdult,
  isChild,
  isDobAFutureDate,
  isGroupChild,
  isRequired,
  validateDays,
  validateMonths,
  validateYear,
} from '../../../../../../../../../util/validators';
import { IStore } from '../../../../../../../../../redux/IStore';
import { IMtaFormNames } from '../../../../../../../../../services/mta/models';
import FieldFormControl from '../../../../../../../../../components/FieldFormControl';
import { TITLES } from '../../../../PersonalDetails/PersonalDetailsForm';
import Select from 'react-select';
import { ErrorDisplay } from '../../../../ErrorDisplay';

type TravellerType = 'child' | 'adult';

interface ITravellerTypeConfig {
  min: number;
  max: number;
}

interface IGroupType {
  adult: ITravellerTypeConfig;
  child?: ITravellerTypeConfig;
  travellersMax?: number;
}

interface IGroupTypeMap {
  [type: string]: IGroupType;
}

const groupTypeTravellerMap: IGroupTypeMap = {
  individual: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  couple: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  family: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 1,
      max: 5,
    },
  },
  family_one_adult: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 1,
      max: 5,
    },
  },
  group: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 9,
    },
    travellersMax: 10,
  },
};

const defaultTraveller = {
  id: '',
  title: '',
  firstName: '',
  lastName: '',
  dob: '',
  phone: '',
  email: '',
  type: '',
  screening: null,
  screeningSessionId: null,
  claims: undefined,
  criminal: undefined,
};

class MTATravellers extends React.Component<any, any> {
  private getAll = (type: TravellerType, fields) => fields.getAll().filter((traveller) => traveller.type === type);
  private count = (type: TravellerType, fields) => this.getAll(type, fields).length;

  public state = {
    showAddressField: false,
    adultTravellerCount: null,
    childTravellerCount: null,
  };

  private handleTravellerUpdates = (type: TravellerType, config: ITravellerTypeConfig, fields, groupType: string) => {
    // Admiral don't want defaults chosen for Family type, so if they are null skip this step until they are filled.
    if (groupType === 'family' && isNull(this.state[`${type}TravellerCount`])) {
      return;
    }

    let total = this.count(type, fields);

    if (total > config.max) {
      // Remove travellers from the form until the maximum amount of travellers are
      // achieved for the next group type.

      forEachRight(this.getAll(type, fields), (traveller) => {
        if (total <= config.max || (total === 1 && type === 'adult')) {
          return;
        }

        this.removeField(traveller.id, fields, type, groupType);
        total -= 1;
      });
    }

    if (total < config.min) {
      const totalToAdd = config.min - total;

      for (let i = 0; i < totalToAdd; i++) {
        this.addField(type, fields, groupType);
        total += 1;
      }
    }

    // Set Family dropdowns to null so they don't default to anything, unless user has already changed it.
    if (groupType === 'family' && !this.state.adultTravellerCount) {
      total = null;
    }

    // Update the Traveller count selector value for adult/child
    this.setState(prevState => ({
      ...prevState,
      [`${type}TravellerCount`]: total,
    }));
  }

  public componentWillReceiveProps(nextProps: any) {
    if (this.props.groupType === nextProps.groupType) {
      return;
    }

    const {fields, groupType} = nextProps;
    const config = groupTypeTravellerMap[groupType ? groupType : ''];

    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields, groupType);
      this.handleTravellerUpdates('child', child, fields, groupType);
    }
  }

  private addField = (type: TravellerType, fields, groupType: string) => {
    const { change } = this.props;

    // Switch to family if adding an adult to family_one_adult
    if (type === 'adult' && groupType === 'family_one_adult') {
      change('data.attributes.metadata.traveller_group', 'family')
    }

    fields.push({
      ...defaultTraveller,
      id: uuid(),
      type,
    });
  }

  private getIndex = (id: string, fields) => {
    let travellerIndex = -1;
    const travellers = fields.getAll();

    travellers.forEach((traveller, index) => {
      if (traveller.id === id) {
        travellerIndex = index;
      }
    });

    if (travellerIndex === -1) {
      console.error('Couldn\'t find traveller', travellers, id, travellerIndex);
    }

    return travellerIndex;
  }

  private removeField = (id: string, fields, type: string, groupType: string) => {
    const { change } = this.props;

    const travellerIndex = this.getIndex(id, fields);

    if (travellerIndex === -1) {
      return;
    }

    // Switch to family_one_adult if removing an adult
    if (type === 'adult' && groupType === 'family' && this.count('adult', fields) === 2) {
      change('data.attributes.metadata.traveller_group', 'family_one_adult')
    }

    fields.remove(travellerIndex);
  }

  private handleAdultChange = (selectedOption) => {
    this.handleTravellerChange('adult', selectedOption)
  }
  private handleChildrenChange = (selectedOption) => {
    this.handleTravellerChange('child', selectedOption)
  }

  private updateDob = (event, value) => {
    // When a DOB element is updated, update the traveller.dob from dobSeparate.
    const { change, formValues } = this.props;
    const match = event.target.name.match(/\[(\d+)\]/);
    const period = event.target.name.split('.').pop();

    const travellerIndex =  match ? match[1] : null;
    const traveller = formValues.travellers[travellerIndex]

    const dob = {
      day: get(traveller, 'dobSeparate.day'),
      month: get(traveller, 'dobSeparate.month'),
      year: get(traveller, 'dobSeparate.year'),
    }

    // Update with new value from field.
    dob[period] = value

    if (dob.day && dob.month && dob.year) {
      // Format into DD/MM/YYYY
      const day = String(dob.day).padStart(2, '0');
      const month = String(dob.month).padStart(2, '0');
      const dateOfBirth = day + '/' + month + '/' + dob.year

      // Set DOB string into data.
      change('data.attributes.travellers[' + travellerIndex + '].dob', dateOfBirth)
    }
  }

  private handleTravellerChange = (type, selectedOption) => {
    const { fields, formValues, metadata, change } = this.props
    const groupType = metadata.traveller_group;
    const previousCount = this.count(type, fields)

    this.setState(prevState => ({
      ...prevState,
      adultTravellerCount: type === 'adult' ? selectedOption.value : prevState.adultTravellerCount,
      childTravellerCount: type === 'child' ? selectedOption.value : prevState.childTravellerCount,
    }));

    // Update the travellers array.
    const adjustment = selectedOption.value - previousCount;

    // Add new travellers.
    if (adjustment > 0) {
      for (let i = 0; i < adjustment; i++) {
        this.addField(type, fields, groupType)
      }
    }

    // Remove travellers.
    if (adjustment < 0) {
      for (let i = 0; i < -adjustment; i++) {
        const travellers = formValues.travellers.filter(trav => {
          return trav.type === type
        })

        // Get id of traveller to be removed
        const id = travellers[travellers.length - 1 - i].id
        this.removeField(id, fields, type, groupType)
      }
    }

    if (type === 'adult' && selectedOption.value === 1 && groupType === 'family') {
      change('data.attributes.metadata.traveller_group', 'family_one_adult')
    }
    if (type === 'adult' && selectedOption.value === 2 && groupType === 'family_one_adult') {
      change('data.attributes.metadata.traveller_group', 'family')
    }
  }

  private renderTravellerCounts() {
    const {groupType, metadata} = this.props;
    const config = groupTypeTravellerMap[groupType ? groupType : ''];

    if (groupType === 'individual' || groupType === 'couple') {
      return null;
    }

    function convertRangeToOptions(type, range) {
      let min = metadata[type].length > range.min ? metadata[type].length : range.min;
      let max = range.max;

      // Adjust family/family_one_adult options so the user can switch between the two under the heading 'Family'
      if (groupType === 'family' && min === 2) {
        min = 1;
      }
      if (groupType === 'family_one_adult' && max === 1) {
        max = 2;
      }

      // Make array of objects for Select element, with numbers between min and max
      return [...Array(max - min + 1)].map((_, i) => {
        return {value: min + i, label: min + i};
      });
    }

    return (
      <div>
        <div style={{margin: '20px 0 20px 0'}}>
          <p className="mta-declaration-body-text">How many people in your group?</p>
        </div>

        <Row className="dynamic-row">
          <Col xs={6}>Adults</Col>
          <Col xs={6}>
            <Select
              name={'test'}
              id={'active-single-question'}
              options={convertRangeToOptions('adults', config.adult)}
              value={this.state.adultTravellerCount}
              clearable={false}
              onChange={this.handleAdultChange}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={6}>Children<br/>or dependents under 18</Col>
          <Col xs={6}>
            <Select
              name={'test'}
              id={'active-single-question'}
              options={convertRangeToOptions('children', config.child)}
              value={this.state.childTravellerCount}
              clearable={false}
              onChange={this.handleChildrenChange}
            />
          </Col>
        </Row>
      </div>
    )
  }


  private renderFields(type: TravellerType) {
    const {fields, i18n, groupType, channel, selectedPolicy } = this.props;
    const travellersCount = selectedPolicy.metadata.adults.length + selectedPolicy.metadata.children.length;
    const isGroup = groupType === 'group';

    // On request of Admiral, dropdowns are null when group type family is first selected.
    // Only show travellers after these are both updated.
    if (groupType === 'family' && (isNull(this.state.adultTravellerCount) || isNull(this.state.childTravellerCount))) {
      return null;
    }

    return this.getAll(type, fields).map((traveller, index) => {
      const fieldIndex = this.getIndex(traveller.id, fields);
      const disableField = travellersCount - fieldIndex > 0;
      let dobError = '';

      if (traveller.dob) {
        const dob = traveller.dob;
        dobError = (type === 'adult')
          ? isAdult(dob)
          : isDobAFutureDate(dob) || (isGroup ? isGroupChild(dob) : isChild(dob));
      }

      return (
        <div key={fieldIndex}>
          <div key={index}>
            <Row className="dynamic-row">
              {index === 0 && type === 'adult' &&
                (<Col xs={12} className="full-width-xs" ><h3>{i18n.quote.tripDetails.traveller.lead}</h3></Col>)}
              {(index > 0 || type !== 'adult') &&
                (<Col xs={12} className="full-width-xs"><h3>{i18n.quote.tripDetails.traveller[type]}
                  &nbsp;{index + 1}</h3></Col>)}
            </Row>
            <Row className={'dynamic-row'}>
              <Col xs={6} className={'full-width-xs'}>Preferred Title</Col>
              <Col xs={6} className={'full-width-xs title-selection-container pl-15'} >
                {map(TITLES, (value, key) => {
                  const isSelected = traveller.title === value;
                  return (
                    <Col xs={4}
                         key={key}
                         className={isSelected ? 'traveller-title-selection selected' : 'traveller-title-selection'}>
                      <div>
                        <Field
                          name={`data.attributes.travellers[${fieldIndex}].title`}
                          id={key + '-btn'}
                          type="radio"
                          value={value}
                          disabled={disableField}
                          component={FieldFormControl}
                          validate={[isRequired]}
                        >
                          <div className={`trip-type-btn ${key}`}>
                            <div className={'trip-type-selection-container'}>
                              {value}
                            </div>
                          </div>
                        </Field>
                      </div>
                    </Col>
                  )
                })}
              </Col>
            </Row>
            <Row className="dynamic-row">
              <Col xs={6} className={'full-width-xs'}>First Name</Col>
              <Col xs={6} className={'full-width-xs'}>
                <Field
                  name={`data.attributes.travellers[${fieldIndex}].firstName`}
                  type="text"
                  placeholder="First Name"
                  component={FieldFormControl}
                  validate={[isRequired, hasEmojis, hasSpecialCharacters]}
                  disabled={disableField}
                  autoComplete="new-password"
                />
              </Col>
            </Row>
            <Row className="dynamic-row">
              <Col xs={6} className={'full-width-xs'}>Last Name</Col>
              <Col xs={6} className={'full-width-xs '}>
                <Field
                  name={`data.attributes.travellers[${fieldIndex}].lastName`}
                  type="text"
                  placeholder="Last Name"
                  component={FieldFormControl}
                  validate={[isRequired, hasEmojis, allowOnlyApostrophes]}
                  disabled={disableField}
                  autoComplete="new-password"
                />
              </Col>
            </Row>
            <Row className="dynamic-row">
              <Col xs={6} className={'full-width-xs'}>Date of Birth</Col>
                  <Col xs={2} className={'full-width-xs'}>
                    <Field
                      name={`data.attributes.travellers[${fieldIndex}].dobSeparate.day`}
                      inputmode="numeric"
                      type="text"
                      maxLength={'2'}
                      placeholder={'DD'}
                      className={'text-center'}
                      component={FieldFormControl}
                      validate={[isRequired, hasEmojis, validateDays]}
                      autocomplete="new-password"
                      disabled={disableField}
                      readOnly={channel.channelType === 'AGG'}
                      onChange={this.updateDob}
                    />
                    <div className={'text-center'}>Day</div>
                  </Col>
                  <Col xs={2} className={'full-width-xs'}>
                    <Field
                      name={`data.attributes.travellers[${fieldIndex}].dobSeparate.month`}
                      inputmode="numeric"
                      type="text"
                      maxLength={'2'}
                      placeholder={'MM'}
                      className={'text-center'}
                      component={FieldFormControl}
                      validate={[isRequired, hasEmojis, validateMonths]}
                      autocomplete="new-password"
                      disabled={disableField}
                      readOnly={channel.channelType === 'AGG'}
                      onChange={this.updateDob}
                    />
                    <div className={'text-center'}>Month</div>
                  </Col>
                  <Col xs={2} className={'full-width-xs'}>
                    <Field
                      name={`data.attributes.travellers[${fieldIndex}].dobSeparate.year`}
                      inputmode="numeric"
                      type="text"
                      maxLength={'4'}
                      placeholder={'YYYY'}
                      className={'text-center'}
                      component={FieldFormControl}
                      validate={[isRequired, hasEmojis, validateYear]}
                      autocomplete="new-password"
                      disabled={disableField}
                      readOnly={channel.channelType === 'AGG'}
                      onChange={this.updateDob}
                    />
                    <div className={'text-center'}>Year</div>
                  </Col>
                  <Col xs={6}/>
                  <Col xs={12} className={'full-width-xs'}>
                    <ErrorDisplay error={dobError}/>
                  </Col>
            </Row>
          </div>
          <hr/>
        </div>
      );
    });
  }

  public componentWillMount() {
    const {fields, groupType} = this.props;
    const fieldLength = fields.length;

    if (fieldLength === 0) {
      this.addField('adult', fields, groupType);
    }

    const config = groupTypeTravellerMap[groupType ? groupType : ''];

    if (config) {
      const { adult, child } = config;

      this.handleTravellerUpdates('adult', adult, fields, groupType);
      this.handleTravellerUpdates('child', child, fields, groupType);
    }
  }

  public render() {
    const {groupType} = this.props;
    const config = groupTypeTravellerMap[groupType ? groupType : ''];

    return (
      <div>
        <div>
          {this.renderTravellerCounts()}
        </div>

        <div>
          <div>
            {this.renderFields('adult')}
          </div>
        </div>
        <div className={config.child.max === 0 ? 'hidden' : ''}>
          <div className="margin-bottom">
            {this.renderFields('child')}
          </div>
        </div>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector(IMtaFormNames.COVER_DETAILS);

    return component;
  },
  connect(
    (state: IStore) => {
      const tripType = selector(state, 'data.attributes.metadata.scheme_type');
      const travellers = selector(state, 'data.attributes.travellers');
      const channel = state.branding.channel;
      const metadata = selector(state, 'data.attributes.metadata');
      const values = selector(
        state,
        'data.attributes',
      );

      return {
        selectedPolicy: state.mta.selectedPolicy,
        tripType,
        travellers,
        channel,
        metadata,
        formValues: values,
      };
    },
  ),
])(MTATravellers);
